import { Directive, ElementRef, Renderer2, Input, HostListener, forwardRef, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { PlessViewUtils } from '../utils/pless-view-utils.service';

@Directive({
    selector: 'input[plessInputText],textarea[plessInputText]',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PlessInputTextDirective),
            multi: true
        },
    ]
})

/**
 * Plessテキスト入力(入力制限機能付き)
 */
export class PlessInputTextDirective implements OnInit, ControlValueAccessor, OnChanges {

    /** 入力可能文字を保持する配列 */
    protected restrictArray: Array<string>;

    /** IME入力中かどうかを示すフラグ */
    protected compositionFlag = false;

    /** 入力可能文字 */
    @Input() restrict: string;

    /** 全角のみ入力可能かどうか */
    @Input() fullOnly = false;

    /** 半角のみ入力可能かどうか */
    @Input() halfOnly = false;

    /** タッチイベントハンドラ */
    onChange: (value: any) => void;

    /** チェンジイベントハンドラ */
    onTouched: (value: any) => void;

    constructor(protected elementRef: ElementRef, protected renderer: Renderer2) {
    }

    ngOnInit() {
        // 入力可能文字が指定されている場合、指定文字を配列で保持する
        if (this.restrict != null) {
            this.restrictArray = this.getRestrictArray();
        }
    }

    ngOnChanges() {
        /* restrictの動的変更に対応 */
        // 入力可能文字が指定されている場合、指定文字を配列で保持する
        if (this.restrict != null) {
            this.restrictArray = this.getRestrictArray();
        }
    }

    writeValue(value: any) {
        // バインド変数の値を変えても画面上の値が変わらない場合がある為、値をセット
        this.elementRef.nativeElement.value = PlessViewUtils.nullToBlank(value);
        // 入力可能文字に制限する処理を実行
        if (value != null) {
            this.doRestrict(value);
        }
    }

    registerOnChange(fn: any) {
        this.onChange = fn;
    }

    registerOnTouched(fn: any) {
        this.onTouched = fn;
    }

    /** IME入力開始時の処理 */
    @HostListener('compositionstart', ['$event'])
    oncompositionstart(event: CompositionEvent) {
        this.compositionFlag = true;
    }

    /** IME入力終了(確定)時の処理 */
    @HostListener('compositionend', ['$event'])
    oncompositionend(event: CompositionEvent) {
        this.compositionFlag = false;
        // 入力時の処理を実行
        setTimeout(() => {      // すぐに実行するとカーソル位置が戻らないので1ms後に実行
            this.oninput(this.elementRef.nativeElement.value);
        }, 1);
    }

    /**
     * 入力時の処理
     */
    @HostListener('input', ['$event.target.value'])
    oninput(value: string) {
        if (this.compositionFlag) {
            return;     // IME入力中(確定前)は処理しない
        }
        // 入力可能文字および文字数に制限する処理を実行
        this.doRestrict(value);
    }

    /**
     * 入力制限処理
     */
    protected doRestrict(value: string) {
        if (this.restrictArray == null && !this.fullOnly && !this.halfOnly) {
            this.refresh(value);
            return;     // 入力制限が無い場合は処理しない
        }
        if (value == null || value === '') {
            this.refresh(value);
            return;     // 入力値が空の場合は処理しない
        }

        let resultValue = value;
        let resultIndex = this.elementRef.nativeElement.selectionStart;

        // 全角・半角のみ入力可能な場合
        if (this.fullOnly) {
            // 全角のみ入力可能な場合
            const fullValue = PlessViewUtils.halfToFull(value);
            resultIndex -= (value.length - fullValue.length); // "ｶﾞ"→"ガ" など文字数が減った場合はカーソル位置を調整
            resultValue = fullValue;
        } else if (this.halfOnly) {
            // 半角のみ入力可能な場合
            const halfValue = PlessViewUtils.fullToHalf(value);
            resultIndex -= (value.length - halfValue.length); // "ガ"→ "ｶﾞ" など文字数が増えた場合はカーソル位置を調整
            resultValue = halfValue;
        }

        // 入力制限文字がある場合
        if (this.restrictArray != null && this.restrictArray.length > 0) {
            const valueArray = resultValue.split('');
            resultValue = '';
            for (let i = 0; i < valueArray.length; i++) {
                if (this.restrictArray.indexOf(valueArray[i]) >= 0) {
                    // 入力可能文字だけ残す
                    resultValue += valueArray[i];
                    // 項番2231 START 共通モジュール課題管理NO.29対応
                } else if (this.restrictArray.indexOf(valueArray[i].toUpperCase()) >= 0) {
                    // 小文字が入力された場合、大文字が入力可ならば自動的に大文字にする
                    resultValue += valueArray[i].toUpperCase();
                } else if (this.restrictArray.indexOf(valueArray[i].toLowerCase()) >= 0) {
                    // 大文字が入力された場合、子文字が入力可ならば自動的に子文字にする
                    resultValue += valueArray[i].toLowerCase();
                    // 項番2231 END 共通モジュール課題管理NO.29対応
                } else {
                    // 削除した場合はカーソル位置を調整する
                    resultIndex--;
                }
            }
        }

        // 文字を削除した場合は削除後の値を適応
        if (value !== resultValue) {
            this.elementRef.nativeElement.value = resultValue;
            this.elementRef.nativeElement.selectionStart = resultIndex;
            this.elementRef.nativeElement.selectionEnd = resultIndex;
        }

        this.refresh(resultValue);
    }

    /**
     * 入力可能文字を配列で取得
     */
    protected getRestrictArray(): Array<string> {
        if (this.restrict == null) {
            return null;
        }
        let resultRestrict = this.restrict;
        resultRestrict = resultRestrict.replace(/0-9/g, '0123456789');
        resultRestrict = resultRestrict.replace(/a-z/g, 'abcdefghijklmnopqrstuvwxyz');
        resultRestrict = resultRestrict.replace(/A-Z/g, 'ABCDEFGHIJKLMNOPQRSTUVWXYZ');
        resultRestrict += '\n';     // textarea用に改行を許可
        return resultRestrict.split('');
    }

    protected refresh(value: any) {
        if (this.onChange != null) {
            this.onChange(value);
        }
        if (this.onTouched != null) {
            this.onTouched(value);
        }
    }
}
